import { useEffect, useState } from 'react';
import { ChatBubbleOutlined, Close, DeleteOutlined, Send } from '@mui/icons-material';
import { Box, Button, CircularProgress, Fab, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import { signInAnonymously } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { auth } from '../config/data/firebase';
import { useAuth } from '../context/AuthContext';
import { dismissChatThread, getChatBootstrap, sendChatMessage, subscribeToMessages } from './api/chatClient';
import type { ChatMessage } from './engine/types';
import { CHAT_THREAD_STORAGE_KEY } from './sessionTransition';

const ChatWidget = () => {
	const { user } = useAuth();
	const location = useLocation();
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [enabled, setEnabled] = useState(false);
	const [welcomeMessage, setWelcomeMessage] = useState('');
	const [staffOnline, setStaffOnline] = useState(false);
	const [offlineNotice, setOfflineNotice] = useState('');
	const [threadId, setThreadId] = useState<string | undefined>(() => window.localStorage.getItem(CHAT_THREAD_STORAGE_KEY) || undefined);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [body, setBody] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		getChatBootstrap()
			.then((bootstrap) => {
				setEnabled(bootstrap.enabled);
				setWelcomeMessage(bootstrap.welcomeMessage);
				setStaffOnline(bootstrap.staffOnline);
				setOfflineNotice(bootstrap.offlineHumanNotice);
			})
			.catch(() => setEnabled(false));
	}, []);

	useEffect(() => {
		if (!threadId) return;
		return subscribeToMessages(threadId, setMessages);
	}, [threadId]);

	const ensureSession = async () => {
		if (!user) await signInAnonymously(auth);
	};

	const handleOpen = async () => {
		setOpen(true);
		setError('');
		try {
			await ensureSession();
		} catch {
			setError('We could not start a secure chat session. Please try again.');
		}
	};

	const handleSend = async () => {
		const message = body.trim();
		if (!message || loading) return;
		setLoading(true);
		setError('');
		try {
			await ensureSession();
			const result = await sendChatMessage(message, threadId);
			setThreadId(result.threadId);
			window.localStorage.setItem(CHAT_THREAD_STORAGE_KEY, result.threadId);
			setBody('');
		} catch (sendError) {
			setError(sendError instanceof Error ? sendError.message : 'Your message could not be sent.');
		} finally {
			setLoading(false);
		}
	};

	const dismissConversation = async () => {
		if (!threadId) return;
		try {
			await dismissChatThread(threadId);
			window.localStorage.removeItem(CHAT_THREAD_STORAGE_KEY);
			setThreadId(undefined);
			setMessages([]);
			setOpen(false);
		} catch (deleteError) {
			setError(deleteError instanceof Error ? deleteError.message : 'Your conversation could not be deleted.');
		}
	};

	// The full member inbox already provides the complete chat surface.
	// Keeping the launcher off this route prevents it from covering the reply action.
	if (!enabled || location.pathname === '/members/chat') return null;

	return (
		<>
			{open && (
				<Paper elevation={12} aria-label='Live help chat' sx={{ position: 'fixed', zIndex: 1400, right: { xs: 12, sm: 24 }, bottom: { xs: 82, sm: 94 }, width: { xs: 'calc(100vw - 24px)', sm: 360 }, overflow: 'hidden' }}>
					<Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
						<Box>
							<Typography sx={{ fontWeight: 700 }}>Parker Fund Help</Typography>
							<Typography variant='caption'>{staffOnline ? 'Committee members may be available now' : 'Messages are answered by real people'}</Typography>
						</Box>
						<Stack direction='row' spacing={0.5}>
							{threadId && <IconButton color='inherit' aria-label='Delete conversation' onClick={dismissConversation}><DeleteOutlined /></IconButton>}
							<IconButton color='inherit' aria-label='Close chat' onClick={() => setOpen(false)}><Close /></IconButton>
						</Stack>
					</Stack>
					<Stack spacing={1} sx={{ height: 320, overflowY: 'auto', p: 1.5, bgcolor: 'background.default' }}>
						{welcomeMessage && !threadId && <Paper variant='outlined' sx={{ p: 1.25, alignSelf: 'flex-start', maxWidth: '90%' }}><Typography variant='body2'>{welcomeMessage}</Typography></Paper>}
						{!staffOnline && <Typography variant='caption' color='text.secondary'>{offlineNotice}</Typography>}
						{messages.map((message) => (
							<Paper key={message.id} variant='outlined' sx={{ p: 1.25, alignSelf: message.senderId === user?.uid ? 'flex-end' : 'flex-start', maxWidth: '90%', bgcolor: message.fromBot ? 'action.hover' : 'background.paper' }}>
								<Typography variant='body2'>{message.body}</Typography>
							</Paper>
						))}
					</Stack>
					{error && <Typography color='error' variant='caption' sx={{ display: 'block', px: 1.5 }}>{error}</Typography>}
					<Stack direction='row' spacing={1} sx={{ p: 1.25 }}>
						<TextField size='small' fullWidth label='How can we help?' value={body} onChange={(event) => setBody(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); handleSend(); } }} slotProps={{ htmlInput: { maxLength: 2000 } }} />
						<Button variant='contained' aria-label='Send message' onClick={handleSend} disabled={loading || !body.trim()}>{loading ? <CircularProgress size={20} color='inherit' /> : <Send />}</Button>
					</Stack>
				</Paper>
			)}
			{!open && (
				<Fab
					variant='extended'
					aria-label='Open live help chat'
					onClick={handleOpen}
					sx={{
						position: 'fixed',
						right: { xs: 12, sm: 24 },
						bottom: { xs: 16, sm: 24 },
						zIndex: 1300,
						minHeight: 40,
						height: 40,
						px: 1.5,
						bgcolor: 'background.paper',
						color: 'text.secondary',
						border: 1,
						borderColor: 'divider',
						boxShadow: 2,
						'&:hover': { bgcolor: 'action.hover', boxShadow: 4 },
					}}>
				<ChatBubbleOutlined sx={{ mr: 1 }} /> Chat
				</Fab>
			)}
		</>
	);
};

export default ChatWidget;
